/**
 * The contract every format handler implements.
 *
 * Three properties of this interface are doing real work, and none of them are
 * cosmetic:
 *
 * - **`probe` is separate from `open`.** §11 requires the first page, sheet or
 *   slide to appear before the rest is parsed. Separating the two makes that
 *   the shape of the code rather than a discipline someone has to remember; a
 *   handler that only knows how to parse everything at once cannot satisfy this
 *   interface without saying so.
 * - **`exportBytes` is optional.** A handler with no export cannot be edited,
 *   and the UI derives the Edit affordance from its presence. The catalog's
 *   claimed support level and the code cannot disagree (§19).
 * - **`validateExport` is part of the contract, not a nicety.** §13 and §42-27
 *   require exported files to be reopened and checked, so the obligation lives
 *   where it cannot be skipped.
 */
import type {
  ResourceLimits, SecurityAssessment, SupportLevel,
} from '@moduviewer/core';

/** Bytes plus everything detection already established about them. */
export interface FileSource {
  readonly bytes: Uint8Array;
  readonly filename: string;
  /** Catalog id settled by detection, never by the extension alone. */
  readonly formatId: string;
  readonly sizeBytes: number;
}

export interface HandlerContext {
  readonly assessment: SecurityAssessment;
  readonly limits: ResourceLimits;
  /** §33 — parsing must abandon promptly when the user moves on. */
  readonly signal: AbortSignal;
}

export type WarningSeverity = 'info' | 'caution' | 'degraded';

/** Something the user should know. Always a key; §25 forbids literal strings. */
export interface HandlerWarning {
  readonly messageKey: string;
  readonly severity: WarningSeverity;
  /** Technical context for the §31 information screen, never for logs. */
  readonly detail?: string;
}

export type PreviewKind =
  | 'text' | 'table' | 'image' | 'pages' | 'slides' | 'sheets'
  | 'media' | 'archive' | 'model' | 'none';

/**
 * The minimum needed to paint a useful first screen (§11).
 *
 * Everything here must be derivable without parsing the whole file — that is
 * the point. `firstContent` is a fragment, not the document.
 */
export interface Preview {
  readonly kind: PreviewKind;
  /** Pages, rows, slides, entries — whatever the format counts, if cheap. */
  readonly unitCount: number | undefined;
  /** Localised label key for the unit ("viewer.page", "viewer.sheet"). */
  readonly unitKey: string | undefined;
  /** A small piece of real content: first lines, first rows, dimensions. */
  readonly firstContent: unknown;
  readonly warnings: readonly HandlerWarning[];
}

export interface OpenedDocument<TModel> {
  readonly formatId: string;
  readonly model: TModel;
  /** True when this file must not be edited, whatever the catalog says. */
  readonly readOnly: boolean;
  readonly editLevel: SupportLevel;
  /** §13/§24 — what this format cannot keep, shown before editing begins. */
  readonly lossOnSave: readonly string[];
  readonly warnings: readonly HandlerWarning[];
}

export interface ExportResult {
  readonly bytes: Uint8Array;
  /** Suggested name for Save As; the shell may still ask (§13). */
  readonly suggestedName: string;
  readonly formatId: string;
  /** Features known to have been dropped in this specific export. */
  readonly dropped: readonly string[];
}

export interface ValidationReport {
  readonly ok: boolean;
  /** True when the exported bytes were reopened successfully (§42-27). */
  readonly reopened: boolean;
  /** True when reopening produced a model equivalent to what was exported. */
  readonly contentPreserved: boolean;
  readonly issues: readonly string[];
}

export interface FormatHandler<TModel = unknown> {
  readonly id: string;
  /** Catalog format ids this handler serves. */
  readonly formatIds: readonly string[];

  /** Fast, partial. Must not parse the whole file (§11). */
  probe(source: FileSource, ctx: HandlerContext): Promise<Preview>;

  /** Full parse. Always produces a read-only document first (§4.7). */
  open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<TModel>>;

  /** Absent means this format cannot be edited, and the UI reflects that. */
  exportBytes?(
    model: TModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult>;

  /** Reopen exported bytes and confirm they carry the model (§13, §42-27). */
  validateExport?(
    bytes: Uint8Array, model: TModel, ctx: HandlerContext,
  ): Promise<ValidationReport>;
}

/** Thrown when a file cannot be parsed. Carries a §34 message key. */
export class HandlerError extends Error {
  readonly messageKey: string;

  constructor(messageKey: string, detail: string) {
    super(detail);
    this.name = 'HandlerError';
    this.messageKey = messageKey;
  }
}

/** Throws if the operation was cancelled, so parsers can check cheaply. */
export function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new HandlerError('action.cancel', 'parsing was cancelled');
}
